/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { nanoid } from 'nanoid';
import Form from 'react-bootstrap/Form';
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import BillDetail from 'components/modals/BillDetail';
import ModalSuccess from 'components/modals/ModalSuccess';
import ModalDiscountList from 'components/modals/ModalDiscountList';
import ReceiptPrintPopup from './printing-forms/ModalReceiptPrint';

import {
  PayCash,
  PayQrCode,
  PayTranfer,
  PayDelivery,
  PayCredit,
  Staff,
  PayPromotion,
  PayPoint,
  PayDebtors,
  PayOther,
  PayDiscount
} from 'components/payments';

import { paymentHandler, masterHandler, billHandler } from '../../redux/handlers';

const PAYMENT_CHANNELS = {
  DETAIL: 0,
  CASH: 1,
  QR: 2,
  TRANSFER: 3,
  DELIVERY: 4,
  CREDIT: 5,
  DISCOUNT: 6,
  PROMOTION: 7,
  OTHER: 8,
  DEBTORS: 9
};

const DISCOUNT_TYPES = {
  END_BILL: 'endBill',
  COUPON: 'coupon',
  PROMOTION: 'promotion',
  POINT: 'point'
};

const initialState = {
  payment: {
    moneypay: '0',
    paymentObj: [],
    paymentSelect: { paymentChannelID: PAYMENT_CHANNELS.DETAIL },
    paymentSettingDetail: {},
    paymentChannelList: []
  },
  order: {
    orderNo: '',
    paymentNet: '0',
    paymentTotal: '0',
    paymentRemaining: '-0',
    paymentChange: '0'
  },
  discount: {
    discounts: []
  },
  user: {
    staffSelect: false,
    selectedUser: null,
    userInfo: null,
    selectedUserId: null
  },
  provider: {
    provider: null,
    remark: ''
  },
  delivery: {
    deliverySelect: null,
    details: {
      remark: '',
      paymentChannel: 2
    }
  },
  modal: {
    successOpen: false,
    textSuccess: ''
  },
  buttons: {
    payment: true,
    enter: false
  }
};

export default function ModalPayment({
  modalSuccessOpenObj,
  modalOpenObj,
  branchId,
  businessSlug,
  orderData,
  resetOrder
}) {
  const dispatch = useDispatch();

  const paymentSetting = useSelector(state => state.payment.paymentSetting);
  const paymentMethods = useSelector(state => state.master.paymentMethods);
  const billData = useSelector(state => state.bill.data);
  const paymentResponse = useSelector(state => state.bill.paymentResponse);
  const loading = useSelector(state => state.common.loading);

  const [buttonStates, setButtonStates] = useState(initialState.buttons);
  const [paymentData, setPaymentData] = useState(initialState.payment);
  const [orderDetails, setOrderDetails] = useState(initialState.order);
  const [discountStates, setDiscountStates] = useState(initialState.discount);
  const [userStates, setUserStates] = useState(initialState.user);
  const [providerInfo, setProviderInfo] = useState(initialState.provider);
  const [deliveryInfo, setDeliveryInfo] = useState(initialState.delivery);
  const [modalStates, setModalStates] = useState({
    ...initialState.modal,
    discountListOpen: false,
    receiptPrintOpen: false
  });

  const formatNumber = useCallback(value => {
    const number = parseFloat(value);
    return isNaN(number) ? '0' : number.toFixed(2);
  }, []);

  const processPaymentSettings = useCallback(settings => {
    if (!settings || !settings.branch) return {};

    const { branch } = settings;

    return {
      id: settings.id,
      delivery: (branch.branchDeliverySetting || []).map(delivery => ({
        id: delivery.id,
        name: delivery.name,
        logo: delivery.image_path
      })),
      credit: (branch.branchCreditSetting || []).map(credit => ({
        id: credit.id,
        name: credit.bank?.name,
        logo: credit.bank?.image_path
      })),
      bank: (branch.branchBankSetting || []).map(bank => ({
        id: bank.id,
        name: bank.bank?.name,
        logo: bank.bank?.image_path
      })),
      coupons: (branch.branchPOSSetting || []).map(coupon => ({
        id: coupon.id,
        name: coupon.name,
        value: coupon.discount_value,
        discountTypeId: coupon.discount_type === 'percent' ? 2 : 1
      }))
    };
  }, []);

  const createPaymentChannelList = useCallback((settings, methods) => {
    if (!methods || !Array.isArray(methods)) return [];

    const methodsMap = methods.reduce((acc, method) => {
      acc[method.name] = { code: method.code, id: method.id };
      return acc;
    }, {});

    const channels = [
      {
        paymentChannelID: PAYMENT_CHANNELS.DETAIL,
        paymentChannelName: 'รายละเอียด',
        icon: 'detail',
        disabled: false,
        code: 'DETAIL',
        id: methodsMap['รายละเอียด']?.id || null
      },
      {
        paymentChannelID: PAYMENT_CHANNELS.CASH,
        paymentChannelName: 'เงินสด',
        icon: 'cash',
        disabled: settings ? !settings.is_cash_received : true,
        code: methodsMap['เงินสด']?.code || 'CASH',
        id: methodsMap['เงินสด']?.id || null
      },
      {
        paymentChannelID: PAYMENT_CHANNELS.QR,
        paymentChannelName: 'QR',
        icon: 'qr',
        disabled: settings ? !settings.is_qrcode_received : true,
        code: methodsMap['QR Code']?.code || 'QR',
        id: methodsMap['QR Code']?.id || null
      },
      {
        paymentChannelID: PAYMENT_CHANNELS.TRANSFER,
        paymentChannelName: 'เงินโอน',
        icon: 'tranfers',
        disabled: settings ? !settings.is_bank_received : true,
        code: methodsMap['เงินโอน']?.code || 'BANK_TRANSFER',
        id: methodsMap['เงินโอน']?.id || null
      },
      {
        paymentChannelID: PAYMENT_CHANNELS.DELIVERY,
        paymentChannelName: 'Delivery',
        icon: 'delivery',
        disabled: settings ? !settings.is_delivery_received : true,
        code: methodsMap['Delivery']?.code || 'DELIVERY',
        id: methodsMap['Delivery']?.id || null
      },
      {
        paymentChannelID: PAYMENT_CHANNELS.CREDIT,
        paymentChannelName: 'บัตรเครดิต',
        icon: 'credit',
        disabled: settings ? !settings.is_credit_received : true,
        code: methodsMap['บัตรเครดิต']?.code || 'CREDIT_CARD',
        id: methodsMap['บัตรเครดิต']?.id || null
      },
      {
        paymentChannelID: PAYMENT_CHANNELS.DISCOUNT,
        paymentChannelName: 'คูปอง/ส่วนลด',
        icon: 'coupon',
        disabled: false,
        code: methodsMap['คูปอง/ส่วนลด']?.code || 'COUPON',
        id: methodsMap['คูปอง/ส่วนลด']?.id || null
      },
      {
        paymentChannelID: PAYMENT_CHANNELS.PROMOTION,
        paymentChannelName: 'โปรโมชั่น',
        icon: 'promotion',
        disabled: true,
        code: methodsMap['โปรโมชั่น']?.code || 'PPROMOTION',
        id: methodsMap['โปรโมชั่น']?.id || null
      },
      {
        paymentChannelID: PAYMENT_CHANNELS.OTHER,
        paymentChannelName: 'ช่องทางอื่นๆ',
        icon: 'other',
        disabled: false,
        code: methodsMap['ใช้คะแนนแลกส่วนลด']?.code || 'POINT',
        id: methodsMap['ใช้คะแนนแลกส่วนลด']?.id || null
      }
    ];

    return channels;
  }, []);

  const getDiscountByType = useCallback(
    type => {
      return discountStates.discounts.find(discount => discount.discountType === type);
    },
    [discountStates.discounts]
  );

  const calculatePaymentTotals = useCallback(() => {
    const paymentArray = Array.isArray(paymentData.paymentObj) ? paymentData.paymentObj : [];

    const sum = paymentArray.reduce((total, payment) => total + parseFloat(payment.amount || 0), 0);

    let netAmount = parseFloat(orderDetails.paymentNet);

    const remaining = netAmount - sum;
    const isPaymentComplete = remaining <= 0;

    setOrderDetails(prev => ({
      ...prev,
      paymentTotal: formatNumber(sum),
      paymentRemaining: isPaymentComplete ? '0' : `-${formatNumber(remaining)}`,
      paymentChange: isPaymentComplete ? formatNumber(Math.abs(remaining)) : '0'
    }));

    setButtonStates({
      payment: remaining > 0,
      enter: isPaymentComplete
    });
  }, [paymentData.paymentObj, orderDetails.paymentNet, formatNumber]);

  const handleDebtPaymentItem = useCallback((payment, paymentItem) => {
    return {
      ...paymentItem,
      is_debt: true,
      debt_amount: payment.debt_amount?.toString() || payment.amount,
      member_id: payment.member_id || payment.provider?.id || null,
      remark: payment.remark || 'บันทึกลูกหนี้',
      payment_method_id: payment.method_id || payment.channel?.id || paymentItem.payment_method_id
    };
  }, []);

  const handleCashPayment = useCallback(
    (payment, paymentItem) => {
      const result = { ...paymentItem };
      const pointDiscount = getDiscountByType(DISCOUNT_TYPES.POINT);
      const debtDiscount = getDiscountByType(DISCOUNT_TYPES.DEBTORS);

      if (pointDiscount) {
        result.point = pointDiscount.point?.toString() || '0';
        result.member_id = pointDiscount.member_id || null;
        result.price = parseFloat(pointDiscount.discountPrice || 0).toFixed(2);
        result.remark = pointDiscount.discountName || 'คะแนนแลกส่วนลด';
        result.payment_method_id =
          pointDiscount.method_id || payment.channel?.methodId || payment.channel?.id || result.payment_method_id;
      } else if (debtDiscount) {
        result.is_debt = true;
        result.debt_amount = debtDiscount.debt_amount || '0';
        result.member_id = debtDiscount.member_id || null;
        result.price = parseFloat(debtDiscount.discountPrice || 0).toFixed(2);
        result.remark = debtDiscount.discountName || 'บันทึกลูกหนี้';
        result.payment_method_id =
          debtDiscount.method_id || payment.channel?.methodId || payment.channel?.id || result.payment_method_id;
      }

      if (payment.pointInfo) {
        result.point = payment.pointInfo.point?.toString() || result.point;
        result.member_id = payment.pointInfo.member_id || result.member_id;
        result.payment_method_id = payment.pointInfo.method_id || result.payment_method_id;
      }

      return result;
    },
    [getDiscountByType]
  );

  const handleQrPayment = useCallback((payment, paymentItem) => {
    return {
      ...paymentItem,
      trace_number: payment.provider?.id || null
    };
  }, []);

  const handleTransferPayment = useCallback((payment, paymentItem) => {
    return {
      ...paymentItem,
      payment_method_id: payment.channel?.paymentChannelID || null,
      branch_bank_setting_id: payment.provider?.id || null
    };
  }, []);

  const handleDeliveryPayment = useCallback(
    (payment, paymentItem) => {
      const result = { ...paymentItem };

      if (payment.provider) {
        result.branch_delivery_setting_id = payment.provider.id || null;
      }

      result.remark = deliveryInfo.details.remark || payment.remark || null;
      result.delivery_channel = deliveryInfo.details.paymentChannel === 1 ? 'NORMAL' : 'SERVICE_PROVIDER';

      return result;
    },
    [deliveryInfo]
  );

  const handleCreditCardPayment = useCallback(
    (payment, paymentItem) => {
      const result = { ...paymentItem };

      result.payment_method_id = payment.channel?.paymentChannelID || null;
      if (payment.provider) {
        result.branch_credit_setting_id = payment.provider.id || null;
      }

      result.credit_card_channel = payment.card_info && payment.card_info.type === 2 ? 'DEBIT_CARD' : 'CREDIT_CARD';

      if (payment.card_info) {
        result.credit_card_type_id = parseInt(payment.card_info.credit_card_type_id);

        if (payment.card_info.fee === 1 && payment.card_info.feeValue) {
          result.credit_fee = payment.card_info.feeValue.toString();
          result.credit_fee_type = 'percent';
        } else if (payment.card_info.fee === 0) {
          result.credit_fee = '0';
          result.credit_fee_type = 'percent';
        } else if (paymentSetting?.branch?.branchCreditSetting?.[0]?.fee) {
          result.credit_fee = paymentSetting.branch.branchCreditSetting[0].fee.toString();
          result.credit_fee_type = paymentSetting.branch.branchCreditSetting[0].fee_type || 'percent';
        }

        result.credit_card_last_four_digits =
          payment.card_info.credit_card_last_four_digits || payment.card_info.last_digit || null;
        result.trace_number = payment.card_info.trace_number || null;
        result.remark = payment.card_info.remark || payment.remark || null;
      }

      return result;
    },
    [paymentSetting]
  );

  const handleDiscountPayment = useCallback(
    (payment, paymentItem) => {
      const result = { ...paymentItem };

      const endBillDiscount = getDiscountByType(DISCOUNT_TYPES.END_BILL);

      if (endBillDiscount) {
        result.discount = endBillDiscount.amount.toString();
        result.discount_type =
          endBillDiscount?.isPercent || endBillDiscount.discountTypeId === 2 ? 'percent' : 'amount';
        result.price = parseFloat(endBillDiscount.valueRound || endBillDiscount.value || 0).toFixed(2);
        result.remark = endBillDiscount.discountName || 'ส่วนลด';

        if (orderData && orderData.memberID) {
          result.member_id = orderData.memberID;
        }
      } else if (payment.discountInfo) {
        result.discount = payment.discountInfo.amount.toString();
        result.discount_type = payment.discountInfo.discountTypeId === 2 ? 'percent' : 'amount';
        result.price = parseFloat(payment.discountInfo.valueRound || payment.discountInfo.value || 0).toFixed(2);
        result.remark = payment.discountInfo.discountName || 'ส่วนลด';

        if (orderData && orderData.memberID) {
          result.member_id = orderData.memberID;
        }
      }

      return result;
    },
    [getDiscountByType, orderData]
  );

  const handlePromotionPayment = useCallback(
    (payment, paymentItem) => {
      const result = { ...paymentItem };
      const promotionDiscount = getDiscountByType(DISCOUNT_TYPES.PROMOTION);
      if (promotionDiscount) {
        result.discount = promotionDiscount.value.toString();
        result.discount_type = promotionDiscount.discountTypeId === 2 ? 'percent' : 'amount';
      }
      return result;
    },
    [getDiscountByType]
  );

  const handlePointPayment = useCallback((payment, paymentItem) => {
    const result = { ...paymentItem };

    result.payment_method_id = payment.channel?.id || null;

    if (payment.pointInfo) {
      result.point = payment.pointInfo.point?.toString() || '0';
      result.member_id = payment.pointInfo.member_id || null;
      result.price = parseFloat(payment.amount || 0).toFixed(2);
      result.remark = payment.remark || 'คะแนนแลกส่วนลด';
    }

    return result;
  }, []);

  const handleDebtorPayment = useCallback((payment, paymentItem) => {
    const result = { ...paymentItem };

    result.is_debt = true;
    result.debt_amount = payment.debt_amount?.toString() || payment.amount;
    result.member_id = payment.member_id || payment.provider?.id || null;
    result.remark = payment.remark || 'บันทึกลูกหนี้';

    if (payment.provider) {
      result.member_id = payment.provider.id || result.member_id;
    }

    return result;
  }, []);

  const transformPaymentItem = useCallback(
    payment => {
      if (!payment) return null;

      const paymentItem = {
        payment_method_id: payment.channel?.id || payment.method_id || payment.channel.paymentChannelID,
        branch_bank_setting_id: null,
        branch_credit_setting_id: null,
        branch_delivery_setting_id: null,
        delivery_channel: null,
        credit_card_channel: null,
        credit_card_type_id: null,
        credit_fee: null,
        credit_fee_type: null,
        credit_card_last_four_digits: null,
        trace_number: null,
        remark: payment.remark || null,
        discount: null,
        discount_type: null,
        service_charge: null,
        service_charge_type: null,
        point: null,
        is_debt: payment.is_debt || null,
        debt_amount: payment.debt_amount || null,
        price: parseFloat(payment.amount) || 0
      };

      if (payment.is_debt) {
        return handleDebtPaymentItem(payment, paymentItem);
      }
      const channelId = parseInt(payment.channel?.paymentChannelID);
      switch (channelId) {
        case PAYMENT_CHANNELS.CASH:
          return handleCashPayment(payment, paymentItem);
        case PAYMENT_CHANNELS.QR:
          return handleQrPayment(payment, paymentItem);
        case PAYMENT_CHANNELS.TRANSFER:
          return handleTransferPayment(payment, paymentItem);
        case PAYMENT_CHANNELS.DELIVERY:
          return handleDeliveryPayment(payment, paymentItem);
        case PAYMENT_CHANNELS.CREDIT:
          return handleCreditCardPayment(payment, paymentItem);
        case PAYMENT_CHANNELS.DISCOUNT:
          return handleDiscountPayment(payment, paymentItem);
        case PAYMENT_CHANNELS.PROMOTION:
          return handlePromotionPayment(payment, paymentItem);
        case PAYMENT_CHANNELS.OTHER:
          return handlePointPayment(payment, paymentItem);
        case PAYMENT_CHANNELS.DEBTORS:
          return handleDebtorPayment(payment, paymentItem);
        default:
          return paymentItem;
      }
    },
    [
      handleDebtPaymentItem,
      handleCashPayment,
      handleQrPayment,
      handleTransferPayment,
      handleDeliveryPayment,
      handleCreditCardPayment,
      handleDiscountPayment,
      handlePromotionPayment,
      handlePointPayment,
      handleDebtorPayment
    ]
  );

  const refreshChannelList = useCallback(() => {
    if (paymentSetting && Array.isArray(paymentMethods) && paymentMethods.length > 0) {
      const updatePaymentSetting = processPaymentSettings(paymentSetting);
      const paymentChannelList = createPaymentChannelList(paymentSetting, paymentMethods);

      setPaymentData(prev => ({
        ...prev,
        paymentSettingDetail: updatePaymentSetting,
        paymentChannelList
      }));
    }
  }, [paymentSetting, paymentMethods, processPaymentSettings, createPaymentChannelList]);

  const handleReceiptPrintComplete = useCallback(() => {
    setModalStates(prev => ({
      ...prev,
      receiptPrintOpen: false
    }));

    modalSuccessOpenObj.setModalOpen(true);
  }, [modalSuccessOpenObj]);

  const handlePayment = useCallback(() => {
    setModalStates(prev => ({
      ...prev,
      receiptPrintOpen: true
    }));

    modalOpenObj.setModalOpen(false);
  }, [modalOpenObj]);

  const handleAddPayment = useCallback(() => {
    if (!paymentData.paymentSelect || !paymentData.moneypay) return;

    let paymentAmount = paymentData.moneypay;

    if (paymentAmount.endsWith('.')) {
      paymentAmount = paymentAmount.slice(0, -1);
    }

    const amount = parseFloat(paymentAmount);
    if (!isNaN(amount)) {
      paymentAmount = amount.toString();
    }

    const endBillDiscount = getDiscountByType(DISCOUNT_TYPES.END_BILL);
    const isDiscountSecondStep =
      paymentData.paymentSelect.paymentChannelID === PAYMENT_CHANNELS.DISCOUNT &&
      endBillDiscount &&
      endBillDiscount.value > 0;

    const newPaymentItem = {
      id: nanoid(),
      channel: paymentData.paymentSelect,
      amount: paymentAmount,
      provider: providerInfo.provider,
      remark: providerInfo.remark,
      status: 1
    };

    if (isDiscountSecondStep) {
      newPaymentItem.discountInfo = {
        id: endBillDiscount.id,
        name: endBillDiscount.name,
        discountTypeId: endBillDiscount.discountTypeId,
        isPercent: endBillDiscount.isPercent,
        amount: endBillDiscount.amount,
        value: endBillDiscount.value,
        valueRound: endBillDiscount.valueRound
      };
    }

    setPaymentData(prev => ({
      ...prev,
      paymentObj: Array.isArray(prev.paymentObj) ? [...prev.paymentObj, newPaymentItem] : [newPaymentItem],
      moneypay: '0'
    }));
  }, [paymentData.paymentSelect, paymentData.moneypay, providerInfo, getDiscountByType]);

  const removePaymentItem = useCallback(id => {
    setPaymentData(prev => ({
      ...prev,
      paymentObj: Array.isArray(prev.paymentObj) ? prev.paymentObj.filter(item => item.id !== id) : []
    }));
  }, []);

  const clearAllPaymentData = useCallback(() => {
    setPaymentData({
      ...initialState.payment,
      paymentChannelList: paymentData.paymentChannelList,
      paymentSettingDetail: paymentData.paymentSettingDetail
    });

    setDiscountStates({
      discounts: []
    });

    setUserStates({
      ...initialState.user
    });

    setProviderInfo({
      ...initialState.provider
    });

    setDeliveryInfo({
      ...initialState.delivery
    });

    setModalStates({
      successOpen: false,
      textSuccess: '',
      discountListOpen: false,
      receiptPrintOpen: false
    });

    setButtonStates({
      ...initialState.buttons
    });

    setOrderDetails({
      orderNo: '',
      paymentNet: '0',
      paymentTotal: '0',
      paymentRemaining: '-0',
      paymentChange: '0'
    });
  }, [paymentData.paymentChannelList, paymentData.paymentSettingDetail]);

  const handleCompletePayment = useCallback(async () => {
    const paymentArray = Array.isArray(paymentData.paymentObj) ? paymentData.paymentObj : [];

    const validPaymentArray = paymentArray.filter(item => {
      const amount = parseFloat(item.amount || 0);
      return amount > 0 && !isNaN(amount);
    });

    const isFullyPaid = parseFloat(orderDetails.paymentRemaining) >= 0;

    if (validPaymentArray.length === 0 && !isFullyPaid) {
      setModalStates(prev => ({
        ...prev,
        successOpen: true,
        textSuccess: 'กรุณาเลือกช่องทางการชำระเงิน',
        status: 'fail'
      }));
      return;
    }

    const transformedPayments = validPaymentArray.map(transformPaymentItem).filter(Boolean);

    const endBillDiscount = getDiscountByType(DISCOUNT_TYPES.END_BILL);

    if (orderData && orderData.memberID) {
      transformedPayments.forEach(payment => {
        if (!payment.member_id) {
          payment.member_id = orderData.memberID;
        }
      });
    }

    const finalPaymentData = {
      bill_id: orderData.billId,
      discount_amount: (endBillDiscount?.isPercent ? endBillDiscount.value : endBillDiscount?.amount) || 0,
      user_business_group_id: userStates.selectedUser?.id,
      payments: transformedPayments
    };

    const response = await dispatch(billHandler.makePayment(finalPaymentData, businessSlug));

    if (response && (response.code === 500 || response.code === 400)) {
      setModalStates(prev => ({
        ...prev,
        successOpen: true,
        textSuccess: response.message === 'BILL_COMPLETED' ? 'บิลนี้ถูกชำระเรียบแล้ว' : 'กรุณาเลือกช่องทางการชำระเงิน',
        status: 'fail'
      }));
      setTimeout(() => {
        modalOpenObj.setModalOpen(false);
        resetOrder();
      }, 2000);
    } else {
      clearAllPaymentData();

      await dispatch(billHandler.getBillById(businessSlug, orderData.billId));
      handlePayment();
    }
  }, [
    paymentData.paymentObj,
    orderDetails.paymentRemaining,
    orderData,
    userStates.selectedUser,
    transformPaymentItem,
    getDiscountByType,
    dispatch,
    businessSlug,
    handlePayment,
    modalOpenObj,
    resetOrder
  ]);

  const handleChannelSelect = useCallback(channel => {
    setPaymentData(prev => ({
      ...prev,
      paymentSelect: channel
    }));

    setProviderInfo(prev => ({
      ...prev,
      provider: null
    }));

    if (channel.paymentChannelID === PAYMENT_CHANNELS.DELIVERY) {
      setDeliveryInfo({
        deliverySelect: null,
        details: {
          remark: '',
          paymentChannel: 2
        }
      });
    }
  }, []);

  const addDiscount = useCallback(discountData => {
    setDiscountStates(prev => {
      const existingIndex = prev.discounts.findIndex(item => item.discountType === discountData.discountType);

      if (existingIndex !== -1) {
        const updatedDiscounts = [...prev.discounts];
        updatedDiscounts[existingIndex] = discountData;
        return { ...prev, discounts: updatedDiscounts };
      } else {
        return { ...prev, discounts: [...prev.discounts, discountData] };
      }
    });
  }, []);

  const removeDiscount = useCallback(discountType => {
    setDiscountStates(prev => ({
      ...prev,
      discounts: prev.discounts.filter(item => item.discountType !== discountType)
    }));
  }, []);

  const setSelectedUserId = useCallback(id => {
    setUserStates(prev => ({
      ...prev,
      selectedUserId: id
    }));
  }, []);

  const handleAddDebtorPayment = useCallback(paymentItem => {
    setPaymentData(prev => ({
      ...prev,
      paymentObj: Array.isArray(prev.paymentObj) ? [...prev.paymentObj, paymentItem] : [paymentItem],
      moneypay: '0'
    }));
  }, []);

  const handleAddPointPayment = useCallback(paymentItem => {
    setPaymentData(prev => ({
      ...prev,
      paymentObj: Array.isArray(prev.paymentObj) ? [...prev.paymentObj, paymentItem] : [paymentItem],
      moneypay: '0'
    }));
  }, []);

  const renderPaymentComponent = useCallback(() => {
    const { paymentSelect } = paymentData;
    const { staffSelect } = userStates;

    if (staffSelect) {
      return (
        <Staff
          user={{
            setStaffSelect: val => setUserStates(prev => ({ ...prev, staffSelect: val })),
            StaffSelect: staffSelect,
            selectedUser: userStates.selectedUser,
            setSelectedUser: val => setUserStates(prev => ({ ...prev, selectedUser: val })),
            userInfo: userStates.userInfo,
            setUserInfo: val => setUserStates(prev => ({ ...prev, userInfo: val }))
          }}
          ui={{
            modals: {
              open: modalStates.successOpen,
              setOpen: val => setModalStates(prev => ({ ...prev, successOpen: val })),
              setText: val => setModalStates(prev => ({ ...prev, textSuccess: val }))
            }
          }}
        />
      );
    }

    const paymentProps = {
      payment: {
        data: paymentData.paymentObj,
        setData: val => setPaymentData(prev => ({ ...prev, paymentObj: val })),
        selectedMethod: paymentData.paymentSelect,
        setSelectedMethod: val => setPaymentData(prev => ({ ...prev, paymentSelect: val }))
      },
      money: {
        amount: paymentData.moneypay,
        setAmount: val => setPaymentData(prev => ({ ...prev, moneypay: val }))
      },
      actions: {
        completePayment: handlePayment,
        addPayment: handleAddPayment,
        addDebtorPayment: handleAddDebtorPayment,
        addPointPayment: handleAddPointPayment,
        handleCompletePayment: handleCompletePayment
      },
      order: {
        details: orderDetails,
        setDetails: setOrderDetails
      },
      provider: {
        data: providerInfo.provider,
        remark: providerInfo.remark,
        setData: val => setProviderInfo(prev => ({ ...prev, provider: val })),
        setRemark: val => setProviderInfo(prev => ({ ...prev, remark: val }))
      },
      delivery: {
        data: deliveryInfo.deliverySelect,
        details: deliveryInfo.details,
        setData: val => setDeliveryInfo(prev => ({ ...prev, deliverySelect: val })),
        setDetails: val => setDeliveryInfo(prev => ({ ...prev, details: val }))
      },
      ui: {
        buttons: {
          enterDisabled: !buttonStates.enter,
          paymentDisabled: buttonStates.payment
        },
        modals: {
          open: modalStates.successOpen,
          setOpen: val => setModalStates(prev => ({ ...prev, successOpen: val })),
          setText: val => setModalStates(prev => ({ ...prev, textSuccess: val }))
        }
      },
      discount: {
        list: discountStates.discounts,
        add: addDiscount,
        remove: removeDiscount,
        getByType: getDiscountByType
      },
      settings: {
        paymentSettings: paymentData.paymentSettingDetail,
        refreshList: refreshChannelList
      },
      modalSuccess: modalSuccessOpenObj,
      orderData: orderData
    };

    switch (parseInt(paymentSelect.paymentChannelID)) {
      case PAYMENT_CHANNELS.DETAIL:
        return (
          <BillDetail
            staffObj={{
              setStaffSelect: val => setUserStates(prev => ({ ...prev, staffSelect: val })),
              StaffSelect: staffSelect
            }}
            selectedUserObj={{
              selectedUser: userStates.selectedUser,
              setSelectedUser: val => setUserStates(prev => ({ ...prev, selectedUser: val }))
            }}
            userInfoObj={{
              userInfo: userStates.userInfo,
              setUserInfo: val => setUserStates(prev => ({ ...prev, userInfo: val })),
              setSelectedUserId: setSelectedUserId
            }}
            discountObj={{
              discounts: discountStates.discounts,
              getByType: getDiscountByType
            }}
          />
        );
      case PAYMENT_CHANNELS.CASH:
        return <PayCash {...paymentProps} />;
      case PAYMENT_CHANNELS.QR:
        return <PayQrCode {...paymentProps} />;
      case PAYMENT_CHANNELS.TRANSFER:
        return <PayTranfer {...paymentProps} />;
      case PAYMENT_CHANNELS.DELIVERY:
        return <PayDelivery {...paymentProps} />;
      case PAYMENT_CHANNELS.CREDIT:
        return <PayCredit {...paymentProps} />;
      case PAYMENT_CHANNELS.DISCOUNT:
        return <PayDiscount {...paymentProps} />;
      case PAYMENT_CHANNELS.PROMOTION:
        return <PayPromotion {...paymentProps} />;
      case PAYMENT_CHANNELS.OTHER:
        return <PayPoint {...paymentProps} />;
      case PAYMENT_CHANNELS.DEBTORS:
        return <PayDebtors {...paymentProps} />;
      default:
        if ([PAYMENT_CHANNELS.OTHER, PAYMENT_CHANNELS.DEBTORS].includes(parseInt(paymentSelect.paymentChannelID))) {
          return (
            <PayOther
              PaymentSelect={paymentData.paymentSelect}
              setPaymentSelect={val => setPaymentData(prev => ({ ...prev, paymentSelect: val }))}
              setProvider={val => setProviderInfo(prev => ({ ...prev, provider: val }))}
            />
          );
        }
        return null;
    }
  }, [
    paymentData,
    userStates,
    providerInfo,
    orderDetails,
    modalStates,
    buttonStates,
    discountStates,
    deliveryInfo,
    handlePayment,
    handleAddPayment,
    handleCompletePayment,
    refreshChannelList,
    modalSuccessOpenObj,
    getDiscountByType,
    addDiscount,
    removeDiscount,
    setSelectedUserId,
    handleAddDebtorPayment,
    handleAddPointPayment,
    orderData
  ]);

  const renderDeliveryForm = useCallback(() => {
    return (
      <div className="px-3 py-3">
        <div className="mb-4">
          <div className="fs-16 fw-500 text-[#061126] mb-3">Remark</div>
          <input
            className="form-control border-0"
            style={{
              background: '#FFF',
              borderRadius: '8px',
              filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.14))'
            }}
            onChange={e => {
              setDeliveryInfo(prev => ({
                ...prev,
                details: {
                  ...prev.details,
                  remark: e.target.value
                }
              }));
            }}
          />
        </div>
        <div className="mb-2">
          <div className="fs-16 fw-500 text-[#061126] mb-3">ช่องทางการชำระ</div>
          <div>
            <Form.Check
              inline
              label="ชำระผ่านผู้ให้บริการ"
              name="paymentDeliveryChannel"
              type="radio"
              id="paymentDeliveryChannel2"
              className="form-check-st-5 tc-7"
              checked={deliveryInfo.details.paymentChannel === 2}
              value={2}
              onChange={() => {
                setDeliveryInfo(prev => ({
                  ...prev,
                  details: {
                    ...prev.details,
                    paymentChannel: 2
                  }
                }));
              }}
            />
          </div>
        </div>
      </div>
    );
  }, [deliveryInfo.details]);

  const PaymentItem = useCallback(({ item, handleClick }) => {
    const showSuccessText = [PAYMENT_CHANNELS.TRANSFER, PAYMENT_CHANNELS.DELIVERY, PAYMENT_CHANNELS.CREDIT].includes(
      parseInt(item.channel.paymentChannelID)
    );

    return (
      <li className="d-flex align-items-center">
        <div
          className={`me-3 overflow-hidden ${item.provider ? 'me-3' : 'me-auto'}`}
          style={{
            flex: `0 0 ${item.provider?.logo ? '60px' : '120px'}`,
            width: `${item.provider?.logo ? '60px' : '120px'}`
          }}
        >
          {item.channel.paymentChannelName}
        </div>

        {item.provider && (
          <div className="me-auto d-flex align-items-center">
            {item.provider.logo && (
              <img
                src={item.provider.logo}
                style={{
                  width: '18px',
                  height: '18px',
                  objectFit: 'cover',
                  borderRadius: '50%'
                }}
                alt={item.provider.name}
              />
            )}
            <div className="text-[#061126] fs-14 ms-2">{item.provider.name}</div>
          </div>
        )}

        {showSuccessText && <div className="me-auto text-[#00BC3A] fs-14">สำเร็จ</div>}

        <div className="text-end" style={{ flex: '0 0 65px', width: '65px' }}>
          {item.amount}
        </div>
        <button type="button" className="btn btn-close-st-1 ms-2" onClick={handleClick}></button>
      </li>
    );
  }, []);

  const renderPaymentList = useCallback(() => {
    const paymentItems = Array.isArray(paymentData.paymentObj) ? paymentData.paymentObj : [];

    const validPaymentItems = paymentItems.filter(item => {
      const amount = parseFloat(item.amount || 0);
      return amount > 0 && !isNaN(amount);
    });

    return (
      <div className="card-body px-2 py-2 text-[#061126]">
        <div className="bx-paylist pt-2">
          <ul className="paylist">
            {validPaymentItems.length > 0 &&
              validPaymentItems.map(item => (
                <PaymentItem key={item.id} item={item} handleClick={() => removePaymentItem(item.id)} />
              ))}
            {validPaymentItems.length === 0 && (
              <li className="text-center text-muted py-3">
                <div className="fs-14">ยังไม่มีรายการชำระเงิน</div>
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  }, [paymentData, PaymentItem, removePaymentItem]);

  const renderPaymentButtons = useCallback(() => {
    if (!Array.isArray(paymentData.paymentChannelList)) return null;

    return (
      <div className="row mb-0">
        {paymentData.paymentChannelList.map((item, index) => (
          <div className="col-4" key={index}>
            <button
              className={`btn btn-select-payment ${item.icon} ${
                paymentData.paymentSelect.paymentChannelID === item.paymentChannelID ? 'active' : ''
              }`}
              onClick={() => handleChannelSelect(item)}
              disabled={item.disabled}
            >
              <div className="pay-icon"></div>
              <div>{item.paymentChannelName}</div>
            </button>
          </div>
        ))}
      </div>
    );
  }, [paymentData.paymentChannelList, paymentData.paymentSelect.paymentChannelID, handleChannelSelect]);

  const renderOrderSummary = useCallback(() => {
    const endBillDiscount = getDiscountByType(DISCOUNT_TYPES.END_BILL);

    const hasEndBillDiscount =
      endBillDiscount && (endBillDiscount.id || (endBillDiscount.amount && endBillDiscount.amount > 0));

    const getEndBillDiscountTagText = () => {
      if (!hasEndBillDiscount) return null;

      if (endBillDiscount.name) {
        return endBillDiscount.name;
      } else if (endBillDiscount?.isPercent || endBillDiscount.discountTypeId === 2) {
        return `ส่วนลด ${endBillDiscount.amount} %`;
      } else {
        return `ส่วนลด ${endBillDiscount.amount} บาท`;
      }
    };

    const handleRemoveDiscount = discountType => {
      removeDiscount(discountType);
    };

    const calculateNetAmount = () => {
      const originalAmount = parseFloat(orderDetails.paymentNet);
      return originalAmount;
    };

    const allDiscounts = discountStates.discounts;
    const hasAnyDiscount = allDiscounts.length > 0;

    const openDiscountListModal = () => {
      if (hasAnyDiscount) {
        setModalStates(prev => ({
          ...prev,
          discountListOpen: true
        }));
      }
    };

    return (
      <div className="card-body py-2 text-[#061126]">
        <div className="mb-1 d-flex align-items-center">
          <div className="me-auto fs-14 pt-2">มูลค่าที่ต้องชำระสุทธิ</div>
          <div className="text-end">
            <div className="fs-24">{calculateNetAmount().toFixed(2)}</div>
          </div>
        </div>

        <div className="mb-1 d-flex align-items-center">
          <div className="me-auto flex-fill">
            {hasEndBillDiscount && (
              <div
                className="p-1 bg-[#FFD8DA] fs-12 text-[#F95057] d-inline-block me-3 mb-1"
                style={{ borderRadius: '2px' }}
              >
                {getEndBillDiscountTagText()}
                <span
                  className="ms-2"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleRemoveDiscount(DISCOUNT_TYPES.END_BILL)}
                >
                  ×
                </span>
              </div>
            )}

            {discountStates.discounts
              .filter(d => d.discountType !== DISCOUNT_TYPES.END_BILL && d.discountType !== DISCOUNT_TYPES.POINT)
              .map((discount, index) => (
                <div
                  key={index}
                  className="p-1 bg-[#FFD8DA] fs-12 text-[#F95057] d-inline-block me-3 mb-1"
                  style={{ borderRadius: '2px' }}
                >
                  {discount.discountName || `ส่วนลด ${discount.discountType}`}
                  <span
                    className="ms-2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => removeDiscount(discount.discountType)}
                  >
                    ×
                  </span>
                </div>
              ))}
          </div>
          <div className="">
            <button className="btn btn-c2" disabled={!hasAnyDiscount} onClick={openDiscountListModal}>
              ดูทั้งหมด
            </button>
          </div>
        </div>

        <div className="mb-1 d-flex align-items-center">
          <div className="me-auto fs-14">มูลค่าที่ชำระรวม</div>
          <div className="fs-24">{orderDetails.paymentTotal}</div>
        </div>
        <div className="mb-1 d-flex align-items-center">
          <div className="me-auto fs-14">คงเหลือที่ต้องชำระ</div>
          {parseFloat(orderDetails.paymentRemaining) < 0 ? (
            <div className="fs-24 text-[#F00]">{orderDetails.paymentRemaining}</div>
          ) : (
            <div className="fs-24">0</div>
          )}
        </div>
      </div>
    );
  }, [orderDetails, discountStates.discounts, getDiscountByType, removeDiscount, setModalStates]);

  useEffect(() => {
    setPaymentData(prev => {
      if (!Array.isArray(prev.paymentObj)) {
        return {
          ...prev,
          paymentObj: []
        };
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    const loadPaymentData = async () => {
      if (modalOpenObj.modalOpen && orderData?.billId) {
        const endBillDiscount = getDiscountByType(DISCOUNT_TYPES.END_BILL);
        const discountPrice = endBillDiscount && endBillDiscount.value ? parseFloat(endBillDiscount.value) : 0;

        try {
          await Promise.all([
            dispatch(paymentHandler.getPaymentSetting(businessSlug, branchId)),
            dispatch(masterHandler.getPaymentMethods()),
            dispatch(billHandler.getBillById(businessSlug, orderData.billId, discountPrice))
          ]);
        } catch (error) {
          console.error('Error loading payment data:', error);
        }
      }
    };

    loadPaymentData();
  }, [modalOpenObj.modalOpen, orderData, businessSlug, branchId, dispatch, getDiscountByType]);

  useEffect(() => {
    if (billData) {
      const finalAmount = parseFloat(billData.final_profit_loss_rounded || 0);
      const beforeDiscountAmount = parseFloat(billData.total_before_discount || 0);
      setOrderDetails({
        orderNo: billData.order?.code || '',
        paymentNet: formatNumber(finalAmount),
        paymentTotal: '0',
        paymentRemaining: `-${formatNumber(finalAmount)}`,
        paymentChange: '0',
        total_before_discount: formatNumber(beforeDiscountAmount)
      });
    }
  }, [billData, formatNumber]);

  useEffect(() => {
    refreshChannelList();
  }, [refreshChannelList]);

  useEffect(() => {
    calculatePaymentTotals();
  }, [calculatePaymentTotals]);

  useEffect(() => {
    if (modalOpenObj.modalOpen) {
      const endBillDiscount = getDiscountByType(DISCOUNT_TYPES.END_BILL);
      if (endBillDiscount) {
        removeDiscount(DISCOUNT_TYPES.END_BILL);
      }
      setPaymentData({
        ...initialState.payment,
        paymentChannelList: paymentData.paymentChannelList,
        paymentSettingDetail: paymentData.paymentSettingDetail
      });

      setDiscountStates({
        discounts: []
      });

      setUserStates({
        ...initialState.user
      });

      setProviderInfo({
        ...initialState.provider
      });

      setDeliveryInfo({
        ...initialState.delivery
      });

      setModalStates({
        successOpen: false,
        textSuccess: '',
        discountListOpen: false,
        receiptPrintOpen: false
      });

      setButtonStates({
        ...initialState.buttons
      });
    }
  }, [modalOpenObj.modalOpen]);

  useEffect(() => {
    const style = document.createElement('style');
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={modalOpenObj.modalOpen}
        onClose={() => {
          setPaymentData({
            ...initialState.payment,
            paymentChannelList: paymentData.paymentChannelList,
            paymentSettingDetail: paymentData.paymentSettingDetail
          });

          setDiscountStates({
            discounts: []
          });

          setUserStates({
            ...initialState.user
          });

          setProviderInfo({
            ...initialState.provider
          });

          setDeliveryInfo({
            ...initialState.delivery
          });

          setModalStates({
            successOpen: false,
            textSuccess: '',
            discountListOpen: false,
            receiptPrintOpen: false
          });

          setButtonStates({
            ...initialState.buttons
          });
          modalOpenObj.setModalOpen(false);
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500
          }
        }}
      >
        <Fade in={modalOpenObj.modalOpen && !loading}>
          <div className="bg-white focus-visible-none w-[min(90.11vw,905px)] pt-1 p-[min(1.74vw,15px)] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] rounded-[min(1.46vw,15px)] text-16 text-[var(--color-black-2)]">
            <div className="text-end mb-2">
              <button
                className="btn btn-close-st-2"
                onClick={() => {
                  setPaymentData({
                    ...initialState.payment,
                    paymentChannelList: paymentData.paymentChannelList,
                    paymentSettingDetail: paymentData.paymentSettingDetail
                  });

                  setDiscountStates({
                    discounts: []
                  });

                  setUserStates({
                    ...initialState.user
                  });

                  setProviderInfo({
                    ...initialState.provider
                  });

                  setDeliveryInfo({
                    ...initialState.delivery
                  });

                  setModalStates({
                    successOpen: false,
                    textSuccess: '',
                    discountListOpen: false,
                    receiptPrintOpen: false
                  });

                  setButtonStates({
                    ...initialState.buttons
                  });
                  modalOpenObj.setModalOpen(false);
                }}
              ></button>
            </div>
            <div className="row gx-2">
              <div className="col-6 px-4">
                {paymentData.paymentSelect.paymentChannelID === PAYMENT_CHANNELS.OTHER ||
                paymentData.paymentSelect.paymentChannelID === PAYMENT_CHANNELS.DEBTORS ? (
                  <PayOther
                    PaymentSelect={paymentData.paymentSelect}
                    setPaymentSelect={val =>
                      setPaymentData(prev => ({
                        ...prev,
                        paymentSelect: val
                      }))
                    }
                    setProvider={val => setProviderInfo(prev => ({ ...prev, provider: val }))}
                  />
                ) : (
                  renderPaymentButtons()
                )}
                <div className="card card-st-1">
                  {renderOrderSummary()}

                  <hr className="my-0" />

                  {paymentData.paymentSelect.paymentChannelID === PAYMENT_CHANNELS.DELIVERY && !providerInfo.provider
                    ? renderDeliveryForm()
                    : renderPaymentList()}
                </div>
              </div>
              <div className="col-6">{renderPaymentComponent()}</div>
            </div>
          </div>
        </Fade>
      </Modal>

      <ModalSuccess
        modalOpenObj={{
          modalOpen: modalStates.successOpen,
          setModalOpen: val => setModalStates(prev => ({ ...prev, successOpen: val }))
        }}
        message={modalStates.textSuccess || 'ดำเนินการสำเร็จ'}
        status={modalStates.status}
        autoCloseDelay={2000}
      />

      <ModalDiscountList
        modalOpenObj={{
          modalOpen: modalStates.discountListOpen,
          setModalOpen: val => setModalStates(prev => ({ ...prev, discountListOpen: val }))
        }}
        discounts={discountStates.discounts}
        removeDiscount={removeDiscount}
      />

      <ReceiptPrintPopup
        isOpen={modalStates.receiptPrintOpen}
        onPrintComplete={handleReceiptPrintComplete}
        paymentResponse={paymentResponse}
      />
    </div>
  );
}
